import { useMutation } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { createProduct, updateProduct } from '../../services/products';
import { uploadItemImages } from '../../services/items';
import {
  ProductFormValues,
  buildProductPayload,
} from '../../utils/catalog/productForm';
import { ImageItem } from '../../utils/form/appointmentForm';
import client from '../../utils/react-query/client';

function resolveImageUrl(image: ImageItem, uploadedById: Map<string, string>) {
  if (image.isUploaded) return image.url;
  const url = uploadedById.get(image.id);
  if (!url) throw new Error('Image upload failed');
  return url;
}

interface Options {
  onSuccess?: (result: { id: string }, type: 'create' | 'update') => void;
  onError?: (message: string) => void;
}

function useSaveProduct({ onSuccess, onError }: Options = {}) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async ({
      productId,
      values,
      existingItemId,
    }: {
      productId?: string | null;
      values: ProductFormValues;
      existingItemId?: string;
    }) => {
      const businessId = user?.business?.id;
      if (!businessId) throw new Error('No business ID found');

      const images = values.images as ImageItem[];
      const variantImages = values.variants.map(row => row.image);
      const pending = [...images, ...variantImages].filter(
        (image): image is ImageItem => !!image && !image.isUploaded,
      );
      const uploaded = pending.length
        ? await uploadItemImages(pending, businessId)
        : [];
      const uploadedById = new Map(
        pending.map((image, i) => [image.id, uploaded[i]]),
      );
      const payload = buildProductPayload(
        values,
        images.map(image => resolveImageUrl(image, uploadedById)),
        variantImages.map(image =>
          image ? resolveImageUrl(image, uploadedById) : null,
        ),
      );
      if (productId) {
        const result = await updateProduct(productId, businessId, payload);
        await client.invalidateQueries({ queryKey: ['product', productId] });
        return { result, type: 'update' as const };
      }
      const result = await createProduct(businessId, {
        ...payload,
        existingItemId,
      });
      return { result, type: 'create' as const };
    },
    onSuccess: ({ result, type }) => onSuccess?.(result, type),
    onError: () => onError?.('Failed to save product'),
  });
}

export default useSaveProduct;
